// @givmo/board — Settings → Organization → Connected accounts: every account outside Givmo the
// organization lets Givmo use (CBG-CAL1 slice 1d; the approved frames `org-accounts`,
// `org-accounts-attention`, `org-accounts-disconnect`).
//
// For the settings authority (`write_settings`, a Global Admin's, which no module switch masks): the
// host declares it as an Organization card on that capability. Served by core's
// `GET /org/{org_ref}/integrations?purpose=board_meetings` — the read takes ONE purpose, and
// `board_meetings` is the only one — and `DELETE …/{id}`. It is the one place a Global Admin can still
// see and disconnect these accounts while Board is switched off ("Revoking access must never depend on
// Board being on"). No Connect button: an account is connected where it is used (question 1).
//
// ⚠ BOARD OFF (the orchestrator's condition, 2026-10-07): where Board's module is off for the
// organization, the card makes no Board read, and a disconnect offers none of Board's choices and
// calls no Board route — core's plain confirmation and `DELETE` only. Board on, a calendar that still
// sends meetings gets Board's choice first (`DisconnectDialog`).
//
// DARK until served, and until there is something to show: core's list answers the route miss while
// `ORG_INTEGRATIONS_ENABLED` is off, and the card renders only once at least one account is connected.
//
// Each row names its account as CBG-UX14 drew it: whose it is first, then its address with the
// personal mark. On Board's own door the host leaves this card out, where Board's "Calendar" card
// holds the calendars the board's meetings go out from (Q2).

import { useState } from 'react';
import type { OrgIntegrationConnectionOut } from '@givmo/api-client';
import { Button, Card, CardHeader, Dialog, RowActions } from '@givmo/design-system';
import { useQuery } from '@tanstack/react-query';
import { BoardOfficerProvider } from '../../board-host';
import { useOptionalBoardConnectionOperations } from '../../board-runtime';
import { CALENDAR_PROVIDER } from '../../calendar/calendar-connect';
import { meetingsNamed, useMeetingsById, useOfficerNames } from '../../calendar/calendar-model';
import {
  orgConnectionsQuery,
  useBoardCalendarConnections,
  useCalendarSenders,
  useOrgConnections,
} from '../../calendar/calendar-queries';
import { calendarWords as w, providerName } from '../../calendar/calendar-words';
import { useCalendarConnect } from '../../calendar/use-calendar-connect';
import { DisconnectDialog } from './account-dialogs';
import { AccountRow, CardTitle } from './calendar-parts';
import { ConnectAnswer } from './connect-dialog';

export interface ConnectedAccountsCardProps {
  orgRef: string;
  /** The person's settled permission set off the console's standing read. */
  permissions: readonly string[];
  /** The person's backend user id; null while unread. */
  userId: string | null;
  /** The organization's name; null while unread. */
  orgName: string | null;
  /**
   * Board is on for the organization. Off: no Board read, and no Board choice before a disconnect.
   */
  boardOn: boolean;
}

/**
 * Whether the card has anything to show: core serves its list and lists a connected account. A host
 * declares the card into the composer only once this is true (CBG-CAL1 slice 1d), so a dark card
 * never brings an empty "Organization" section with it — the composer draws a section for every
 * declared card a person holds, before the card itself can say it renders nothing. The same read
 * as the card's own, so the card then renders from it at once.
 */
export function useConnectedAccountsShown(orgRef: string | null, enabled: boolean): boolean {
  // The console's Settings page is not Board's: where no Board runtime is mounted it reads nothing,
  // and the card stays out.
  const port = useOptionalBoardConnectionOperations();
  const reads = enabled && orgRef !== null && port !== null;
  const connections = useQuery({ ...orgConnectionsQuery(port, orgRef), enabled: reads });
  return reads && (connections.data ?? []).some((row) => row.status !== 'disconnected');
}

function statusFor(row: OrgIntegrationConnectionOut): string | undefined {
  if (row.status === 'needs_reconnect') return w.needsReconnecting;
  if (row.status === 'organizer_left') return w.noLongerHasAccess;
  return undefined;
}

export function ConnectedAccountsCard(props: ConnectedAccountsCardProps) {
  return (
    <BoardOfficerProvider
      host={{
        orgRef: props.orgRef,
        isReady: true,
        isPending: false,
        isError: false,
        error: null,
        permissions: props.permissions,
        userId: props.userId,
        orgName: props.orgName,
      }}
    >
      <ConnectedAccounts {...props} />
    </BoardOfficerProvider>
  );
}

function ConnectedAccounts({ orgRef, userId, orgName, boardOn }: ConnectedAccountsCardProps) {
  const connections = useOrgConnections(orgRef);
  const rows = (connections.data ?? []).filter((row) => row.status !== 'disconnected');
  const shows = rows.length > 0;
  // Board's reads only while Board is on: who sends which meetings, and what Google offers.
  const boardRows = useBoardCalendarConnections(orgRef, boardOn && shows);
  const senders = useCalendarSenders(orgRef, boardOn && shows);
  const meetings = useMeetingsById(orgRef, boardOn && shows && boardRows.data != null);
  const names = useOfficerNames(orgRef, shows);
  const flow = useCalendarConnect(orgRef);
  const [disconnecting, setDisconnecting] = useState<OrgIntegrationConnectionOut | null>(null);

  // Board on: wait until Board has said what each calendar sends (or couldn't), so a disconnect
  // never skips Board's choice for a calendar that still sends meetings. A Board read that fails is
  // "unknown", never a reason to withhold revoking access.
  const boardSettled =
    !boardOn ||
    boardRows.isError ||
    (boardRows.isSuccess && (boardRows.data === null || meetings.loaded || meetings.failed));
  if (!shows || !boardSettled) return null;
  const sorted = [...rows].sort((left, right) =>
    left.account_kind === right.account_kind
      ? left.account_email.localeCompare(right.account_email)
      : left.account_kind === 'board_account'
        ? -1
        : 1,
  );
  const whose = (row: OrgIntegrationConnectionOut) =>
    row.account_kind === 'board_account'
      ? w.boardAccount
      : ((row.owner_user_id ? names.get(row.owner_user_id) : undefined) ?? row.account_email);
  const googleOffered =
    boardOn && (senders.data?.offered_providers ?? []).includes(CALENDAR_PROVIDER);
  /** What Board knows a connection sends; null where Board can't say (off, or not served). */
  const sentBy = (row: OrgIntegrationConnectionOut) => {
    if (!boardOn || boardRows.data == null || !meetings.loaded) return null;
    // Board's read is authoritative for what a connection organizes; the list names the days.
    const ids = (
      boardRows.data.find((entry) => entry.connection_id === row.connection_id)
        ?.upcoming_meeting_ids ?? []
    ).map(String);
    return { ids, meetings: meetingsNamed(ids, meetings.byId) };
  };

  return (
    <Card testId="org-connected-accounts">
      <CardHeader className="flex-col sm:flex-row">
        <CardTitle title={w.orgTitle} lede={w.orgLede} />
      </CardHeader>
      <ul className="divide-y divide-border-subtle">
        {sorted.map((row) => (
          <AccountRow
            key={row.connection_id}
            testId={`org-connected-accounts-${row.connection_id}`}
            primary={whose(row)}
            status={statusFor(row)}
            secondary={[
              `${row.account_email}${row.is_consumer_account ? ` (${w.personalGoogle})` : ''}`,
              providerName(row.provider),
              w.orgFor,
            ]}
            action={
              row.status === 'needs_reconnect' &&
              row.account_kind === 'board_account' &&
              googleOffered ? (
                <Button
                  appearance="saas"
                  tone="primary"
                  size="sm"
                  loading={flow.starting}
                  onClick={() =>
                    flow.begin({
                      accountKind: 'board_account',
                      reconnectConnectionId: row.connection_id,
                      loginHint: row.account_email,
                    })
                  }
                  testId="org-connected-accounts-reconnect"
                >
                  {w.reconnect}
                </Button>
              ) : undefined
            }
            menu={
              <RowActions
                label={w.rowActions(row.account_email)}
                actions={[
                  {
                    label: w.disconnect,
                    destructive: true,
                    onSelect: () => setDisconnecting(row),
                    testId: `org-connected-accounts-${row.connection_id}-disconnect`,
                  },
                ]}
                testId={`org-connected-accounts-${row.connection_id}-actions`}
              />
            }
          />
        ))}
      </ul>

      {flow.outcome !== null ? (
        <Dialog.Root open onOpenChange={(next) => !next && flow.reset()}>
          <Dialog.Content
            aria-label={w.connectBoardTitle}
            width={448}
            className="flex flex-col gap-16"
            testId="calendar-connect-dialog"
          >
            <ConnectAnswer
              outcome={flow.outcome}
              boardAccount
              onClose={flow.reset}
              onAgain={flow.reset}
            />
          </Dialog.Content>
        </Dialog.Root>
      ) : null}
      {disconnecting !== null
        ? (() => {
            const sent = sentBy(disconnecting);
            return (
              <DisconnectDialog
                orgRef={orgRef}
                account={disconnecting}
                own={disconnecting.owner_user_id != null && disconnecting.owner_user_id === userId}
                ownerName={
                  disconnecting.account_kind === 'board_account' ? null : whose(disconnecting)
                }
                meetings={sent === null ? null : sent.meetings}
                meetingCount={sent === null ? undefined : sent.ids.length}
                senders={boardOn ? (senders.data ?? null) : null}
                orgName={orgName ?? ''}
                onClose={() => setDisconnecting(null)}
              />
            );
          })()
        : null}
    </Card>
  );
}
